// src/pages/tender/RaBillGenerator.js

import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  serverTimestamp,
  deleteDoc,
  updateDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../../firebaseConfig";
import toast, { Toaster } from "react-hot-toast";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const cx = (...xs) => xs.filter(Boolean).join(" ");
const toNum = (v) => Number(String(v || "").replace(/[^0-9.\-]/g, "")) || 0;

export default function RaBillGenerator() {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);
  const storage = getStorage();

  const [user, setUser] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [tender, setTender] = useState(null);
  const [loading, setLoading] = useState(true);

  const [rows, setRows] = useState([]);
  const [raDate, setRaDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [saving, setSaving] = useState(false);
  const [existingRaBills, setExistingRaBills] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        toast.error("Please login as Contractor.");
        navigate("/tender?tab=login", { replace: true });
        return;
      }
      setUser(u);
    });
    return () => unsub();
  }, [auth, navigate]);

  const recalcContractorMeasurements = async () => {
    if (!user || !tenderId) return;

    const snap = await getDocs(
      query(
        collection(db, "ra_bills"),
        where("tenderId", "==", tenderId),
        where("vendorUid", "==", user.uid)
      )
    );

    const map = {};
    snap.forEach((d) => {
      const bill = d.data();
      (bill.items || []).forEach((it) => {
        const key = it.itemId || it.boqItemId;
        map[key] = (map[key] || 0) + Number(it.qtyThisRa || it.qty || 0);
      });
    });

    const measSnap = await getDocs(
      collection(db, "tenders", tenderId, "measurements_contractor")
    );

    for (const d of measSnap.docs) {
      const itemId = d.id;
      await updateDoc(d.ref, {
        contractorTotalQty: map[itemId] || 0,
        updatedAt: serverTimestamp(),
      });
    }
  };

  const handleDeleteRaBill = async (bill) => {
    try {
      const status = bill?.status || "pending";

      if (
        status === "approved" ||
        bill.accountingStatus === "ACCOUNTANT_VERIFIED" ||
        bill.accountingStatus === "CEO_APPROVED"
      ) {
        toast.error("Approved/verified RA Bill delete nahi ho sakta.");
        return;
      }

      if (String(bill?.id || "").startsWith("tmp-")) {
        toast.error("Please refresh. Bill sync ho raha hai.");
        setRefreshKey((k) => k + 1);
        return;
      }

      const ok = window.confirm(`Delete ${bill?.raBillNo || bill?.raNo || "RA Bill"} ?`);
      if (!ok) return;

      await deleteDoc(doc(db, "ra_bills", bill.id));
      await recalcContractorMeasurements();

      toast.success("RA Bill deleted & measured reset done.");
      setRefreshKey((k) => k + 1);
    } catch (e) {
      console.error(e);
      toast.error("Delete failed.");
    }
  };

  useEffect(() => {
    const load = async () => {
      if (!user || !tenderId) return;

      try {
        setLoading(true);

        const vSnap = await getDoc(doc(db, "vendors", user.uid));
        if (!vSnap.exists()) {
          toast.error("Vendor profile not found.");
          navigate("/tender?tab=login", { replace: true });
          return;
        }

        const v = { id: vSnap.id, ...vSnap.data() };
        setVendor(v);

        const tSnap = await getDoc(doc(db, "tenders", tenderId));
        if (!tSnap.exists()) {
          toast.error("Tender not found.");
          navigate("/tender/contractor", { replace: true });
          return;
        }

        const t = { id: tSnap.id, ...tSnap.data() };

        if (t.awardedVendorUid && t.awardedVendorUid !== user.uid) {
          toast.error("You are not the awarded contractor for this project.");
          navigate("/tender/contractor", { replace: true });
          return;
        }

        setTender(t);

        const boqSnap = await getDocs(collection(db, "tenders", tenderId, "boq_items"));
        const boqItems = [];
        boqSnap.forEach((d) => boqItems.push({ id: d.id, ...d.data() }));
        boqItems.sort((a, b) => (a.itemNo || 0) - (b.itemNo || 0));

        if (!boqItems.length) toast.error("BOQ master not uploaded for this tender.");

        const qRef = query(
          collection(db, "ra_bills"),
          where("tenderId", "==", tenderId),
          where("vendorUid", "==", user.uid)
        );

        const raSnap = await getDocs(qRef);
        const raList = [];
        raSnap.forEach((d) => raList.push({ id: d.id, ...d.data() }));
        raList.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        setExistingRaBills(raList);

        const prevRaQtyMap = {};
        raList.forEach((bill) => {
          (bill.items || []).forEach((it) => {
            const key = it.itemId || it.boqItemId;
            if (!key) return;
            prevRaQtyMap[key] =
              (prevRaQtyMap[key] || 0) + toNum(it.qtyThisRa || it.qty || 0);
          });
        });

        const measSnap = await getDocs(
          collection(db, "tenders", tenderId, "measurements_contractor")
        );

        const measMap = {};
        measSnap.forEach((d) => {
          const data = d.data();
          const key = data.boqId || d.id;
          measMap[key] = toNum(data.contractorTotalQty || 0);
        });

        const tableRows = boqItems.map((it) => {
          const boqQty = toNum(it.boqQty);
          const rate = toNum(it.rateWithLC || it.rate || 0);

          const measuredTotal = Math.min(
            toNum(measMap[it.id] || it.contractorMeasuredQty || 0),
            boqQty
          );

          const executedPrevRa = toNum(prevRaQtyMap[it.id] || 0);
          const pendingMeasured = Math.max(measuredTotal - executedPrevRa, 0);

          const qtyThisRa = pendingMeasured;
          const amountThisRa = qtyThisRa * rate;

          return {
            id: it.id,
            itemNo: it.itemNo,
            description: it.description,
            unit: it.unit || it.boqUnit || "",
            boqQty,
            rate,
            executedMeasured: measuredTotal,
            executedPrevRa,
            pending: pendingMeasured,
            qtyThisRa,
            amountThisRa,
          };
        });

        setRows(tableRows);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load RA bill data.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user, tenderId, navigate, refreshKey]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/tender?tab=login", { replace: true });
  };

  const raTotal = rows.reduce((sum, r) => sum + (r.amountThisRa || 0), 0);

  const handleGenerateRa = async () => {
    const itemsThisRa = rows.filter((r) => r.qtyThisRa > 0);

    if (!itemsThisRa.length) {
      toast.error("No measured quantity pending for RA.");
      return;
    }

    if (!raDate) {
      toast.error("Select RA bill date.");
      return;
    }

    try {
      setSaving(true);

      const nextIndex = existingRaBills.length + 1;
      const raNo = `RA-${String(nextIndex).padStart(2, "0")}`;
      const vendorName = vendor?.profile?.companyName || vendor?.vendorId || user.email || "";

      const payload = {
        tenderId,
        tenderTitle: tender?.title || tender?.projectName || "",
        projectName: tender?.title || tender?.projectName || "",
        pacsName: tender?.ownerCompany || tender?.pacsName || "",
        vendorUid: user.uid,
        vendorName,
        contractorName: vendorName,
        raNo,
        raBillNo: raNo,
        raDate,
        basicAmount: raTotal,
        totalAmount: raTotal,
        grossAmount: raTotal,
        status: "pending",
        accountingStatus: "PENDING",
        paymentStatus: "PENDING",
        gstInvoiceUrl: "",
        createdAt: serverTimestamp(),
        items: itemsThisRa.map((r) => ({
          itemId: r.id,
          itemNo: r.itemNo,
          description: r.description,
          unit: r.unit,
          boqQty: r.boqQty,
          rate: r.rate,
          executedPrev: r.executedPrevRa,
          qtyThisRa: r.qtyThisRa,
          executedTotal: r.executedPrevRa + r.qtyThisRa,
          amount: r.amountThisRa,
        })),
      };

      const raRef = await addDoc(collection(db, "ra_bills"), payload);

      const pdfBlob = generateRaPdfBlob(tender, {
        ...payload,
        id: raRef.id,
      });

      const filePath = `ra_bills/${tenderId}/${user.uid}/${raNo}_${raRef.id}.pdf`;
      const fileRef = ref(storage, filePath);

      await uploadBytes(fileRef, pdfBlob, {
        contentType: "application/pdf",
      });

      const pdfUrl = await getDownloadURL(fileRef);

      await updateDoc(doc(db, "ra_bills", raRef.id), {
        raBillNo: raNo,
        raBillPdfUrl: pdfUrl,
        raBillPdfPath: filePath,
        raBillPdfName: `${raNo}.pdf`,
        raBillPdfGeneratedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success(`RA Bill ${raNo} generated with PDF.`);

      setRows((prev) =>
        prev.map((r) => ({
          ...r,
          executedPrevRa: r.executedPrevRa + r.qtyThisRa,
          pending: 0,
          qtyThisRa: 0,
          amountThisRa: 0,
        }))
      );

      setExistingRaBills((prev) => [
        ...prev,
        {
          ...payload,
          id: raRef.id,
          raBillPdfUrl: pdfUrl,
          raBillPdfPath: filePath,
          raBillPdfName: `${raNo}.pdf`,
        },
      ]);

      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error(err);
      toast.error("Failed to generate RA bill.");
    } finally {
      setSaving(false);
    }
  };

  const downloadExistingPdf = (bill) => {
    const url = bill.approvedRaBillPdfUrl || bill.raBillPdfUrl;
    if (!url) {
      generateRaPdf(tender, bill);
      return;
    }

    window.open(url, "_blank");
  };

  if (!user || !vendor || !tender) {
    return (
      <>
        <Toaster position="top-center" />
        <div className="min-h-screen flex items-center justify-center bg-emerald-50">
          <div className="text-sm text-slate-600">Loading RA bill…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" />

      <div className="min-h-screen bg-emerald-50 pb-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="rounded-2xl bg-white shadow-lg border border-emerald-200 px-6 py-5 mb-6 flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                RA Bill – Contractor
              </div>
              <h1 className="text-xl md:text-2xl font-extrabold text-emerald-800">
                {tender.title || tender.projectName || "Project"}
              </h1>
              <div className="mt-1 text-[11px] text-slate-600">
                {tender.ownerCompany || ""} · {tender.city || ""}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                Contractor: <b>{vendor?.profile?.companyName || vendor?.vendorId || user.email}</b>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <button
                onClick={handleLogout}
                className="text-xs px-3 py-2 rounded-lg bg-gray-900 text-white shadow hover:bg-black"
              >
                Logout
              </button>
              <Link to="/tender/contractor" className="text-[11px] text-emerald-700 underline">
                ← Back to Contractor Dashboard
              </Link>
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-4">
            <div className="bg-white border border-emerald-100 rounded-xl px-3 py-2 text-[11px]">
              <div className="text-slate-500">RA Bill Date</div>
              <input
                type="date"
                value={raDate}
                onChange={(e) => setRaDate(e.target.value)}
                className="mt-1 px-2 py-1 border rounded-md text-[11px]"
              />
            </div>

            <div className="bg-white border border-emerald-100 rounded-xl px-3 py-2 text-[11px]">
              <div className="text-slate-500">Previous RA Bills</div>
              <div className="font-semibold text-emerald-700">
                {existingRaBills.length} generated
              </div>
            </div>

            <div className="bg-white border border-emerald-100 rounded-xl px-3 py-2 text-[11px] ml-auto">
              <div className="text-slate-500">RA Amount This Bill</div>
              <div className="font-bold text-emerald-800 text-sm">
                ₹ {raTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-3 overflow-auto">
            {rows.length === 0 ? (
              <div className="text-[12px] text-slate-500">
                BOQ items not available. Please contact admin.
              </div>
            ) : (
              <table className="min-w-full text-[11px] border border-slate-200">
                <thead className="bg-emerald-50">
                  <tr>
                    <th className="border border-slate-200 px-2 py-1">Item</th>
                    <th className="border border-slate-200 px-2 py-1 text-left">Description</th>
                    <th className="border border-slate-200 px-2 py-1">Unit</th>
                    <th className="border border-slate-200 px-2 py-1 text-right">BOQ Qty</th>
                    <th className="border border-slate-200 px-2 py-1 text-right">Measured Till Now</th>
                    <th className="border border-slate-200 px-2 py-1 text-right">Pending Measured</th>
                    <th className="border border-slate-200 px-2 py-1 text-right">Rate</th>
                    <th className="border border-slate-200 px-2 py-1 text-right">Qty This RA</th>
                    <th className="border border-slate-200 px-2 py-1 text-right">Amount This RA</th>
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="odd:bg-white even:bg-emerald-50/20">
                      <td className="border border-slate-200 px-2 py-1">{r.itemNo}</td>
                      <td className="border border-slate-200 px-2 py-1 text-left">{r.description}</td>
                      <td className="border border-slate-200 px-2 py-1 text-center">{r.unit}</td>
                      <td className="border border-slate-200 px-2 py-1 text-right">{r.boqQty.toFixed(3)}</td>
                      <td className="border border-slate-200 px-2 py-1 text-right">{r.executedMeasured.toFixed(3)}</td>
                      <td className="border border-slate-200 px-2 py-1 text-right text-emerald-700 font-semibold">
                        {r.pending.toFixed(3)}
                      </td>
                      <td className="border border-slate-200 px-2 py-1 text-right">{r.rate.toFixed(2)}</td>
                      <td className="border border-slate-200 px-2 py-1 text-right">{r.qtyThisRa.toFixed(3)}</td>
                      <td className="border border-slate-200 px-2 py-1 text-right">
                        {r.amountThisRa ? r.amountThisRa.toFixed(2) : "0.00"}
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr className="bg-emerald-100 font-bold">
                    <td colSpan={8} className="border border-slate-200 px-2 py-1 text-right">
                      TOTAL
                    </td>
                    <td className="border border-slate-200 px-2 py-1 text-right">
                      ₹ {raTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={handleGenerateRa}
              disabled={saving || !rows.length}
              className={cx(
                "px-4 py-2 rounded-lg text-xs font-semibold text-white",
                saving || !rows.length
                  ? "bg-emerald-300 cursor-not-allowed"
                  : "bg-emerald-700 hover:bg-emerald-800"
              )}
            >
              {saving ? "Generating RA Bill…" : "Generate RA Bill With PDF"}
            </button>
          </div>

          <div className="mt-6 border border-emerald-200 rounded-2xl bg-white shadow-sm overflow-hidden">
            <div className="px-3 py-2 bg-slate-900 text-[10px] text-white font-semibold flex justify-between">
              <span>Generated RA Bills</span>
            </div>

            <table className="min-w-full text-[10px] border-collapse">
              <thead className="bg-slate-100">
                <tr>
                  <th className="border border-slate-300 px-2 py-1">Sr</th>
                  <th className="border border-slate-300 px-2 py-1 text-left">RA No</th>
                  <th className="border border-slate-300 px-2 py-1">Date</th>
                  <th className="border border-slate-300 px-2 py-1 text-right">Amount</th>
                  <th className="border border-slate-300 px-2 py-1">Accounting</th>
                  <th className="border border-slate-300 px-2 py-1">Payment</th>
                  <th className="border border-slate-300 px-2 py-1">PDF</th>
                  <th className="border border-slate-300 px-2 py-1">Delete</th>
                </tr>
              </thead>

              <tbody>
                {existingRaBills.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="border border-slate-300 px-2 py-2 text-center text-slate-500">
                      No RA bills generated yet.
                    </td>
                  </tr>
                ) : (
                  existingRaBills
                    .slice()
                    .sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0))
                    .map((b, idx) => (
                      <tr key={b.id} className="odd:bg-white even:bg-slate-50">
                        <td className="border border-slate-300 px-2 py-1 text-center">{idx + 1}</td>
                        <td className="border border-slate-300 px-2 py-1 font-bold">
                          {b.raBillNo || b.raNo || "—"}
                        </td>
                        <td className="border border-slate-300 px-2 py-1 text-center">{b.raDate || "—"}</td>
                        <td className="border border-slate-300 px-2 py-1 text-right">
                          ₹ {(b.totalAmount || b.basicAmount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        </td>
                        <td className="border border-slate-300 px-2 py-1 text-center">
                          {b.accountingStatus || "PENDING"}
                        </td>
                        <td className="border border-slate-300 px-2 py-1 text-center">
                          {b.paymentStatus || "PENDING"}
                        </td>
                        <td className="border border-slate-300 px-2 py-1 text-center">
                          <button
                            type="button"
                            onClick={() => downloadExistingPdf(b)}
                            className="px-2 py-0.5 rounded bg-slate-900 text-white"
                          >
                            Download PDF
                          </button>
                        </td>
                        <td className="border border-slate-300 px-2 py-1 text-center">
                          <button
                            type="button"
                            onClick={() => handleDeleteRaBill(b)}
                            disabled={
                              (b.status || "pending") === "approved" ||
                              b.accountingStatus === "ACCOUNTANT_VERIFIED" ||
                              b.accountingStatus === "CEO_APPROVED"
                            }
                            className={cx(
                              "px-2 py-0.5 rounded text-white",
                              (b.status || "pending") === "approved" ||
                                b.accountingStatus === "ACCOUNTANT_VERIFIED" ||
                                b.accountingStatus === "CEO_APPROVED"
                                ? "bg-red-200 cursor-not-allowed"
                                : "bg-red-600 hover:bg-red-700"
                            )}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

/* ----------- download-only old fallback ------------ */
function generateRaPdf(tender, bill) {
  try {
    const blob = generateRaPdfBlob(tender, bill);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${bill.raBillNo || bill.raNo || "RA_Bill"}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error(e);
    toast.error("RA PDF generate karte waqt error aya.");
  }
}

/* ----------- PDF BLOB GENERATOR FOR STORAGE + DOWNLOAD ------------ */
function generateRaPdfBlob(tender, bill) {
  const doc = new jsPDF("p", "mm", "a4");

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const margin = 14;
  const primary = [15, 82, 65];
  const dark = [15, 23, 42];
  const light = [241, 245, 249];

  const title =
    tender?.title || tender?.projectName || bill?.tenderTitle || "Running Account Bill";

  const pacsName =
    tender?.ownerCompany ||
    tender?.pacsName ||
    tender?.pacsShortName ||
    bill?.pacsName ||
    "";

  const contractor = bill.vendorName || bill.contractorName || "";

  const raNo = bill.raBillNo || bill.raNo || "";
  const raDate =
    bill.raDate ||
    (bill.createdAt?.toDate
      ? dayjs(bill.createdAt.toDate()).format("DD/MM/YYYY")
      : dayjs().format("DD/MM/YYYY"));

  const basicAmount = Number(bill.totalAmount || bill.basicAmount || 0);
  const gstAmount = Math.round((basicAmount * 18) / 100);
  const grossAmount = basicAmount + gstAmount;

  const addHeader = () => {
    doc.setFillColor(...dark);
    doc.rect(0, 0, pageW, 18, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("SHINE AGRI ALLIED & MARKETING MULTISTATE COOPERATIVE SOCIETY LTD.", pageW / 2, 8, {
      align: "center",
    });

    doc.setFontSize(8);
    doc.text("PMC Automation System | World's Largest Grain Storage Scheme", pageW / 2, 14, {
      align: "center",
    });

    doc.setTextColor(0, 0, 0);
  };

  const addFooter = () => {
    const pageNo = doc.internal.getNumberOfPages();

    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated on: ${dayjs().format("DD/MM/YYYY HH:mm")}`, margin, pageH - 8);
    doc.text(`Page ${pageNo}`, pageW - margin, pageH - 8, { align: "right" });

    doc.setTextColor(0, 0, 0);
  };

  const addWatermark = () => {
    doc.setTextColor(230, 230, 230);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(36);
    doc.text("SHINE AGRI", pageW / 2, pageH / 2, {
      align: "center",
      angle: 35,
    });
    doc.setTextColor(0, 0, 0);
  };

  addHeader();
  addWatermark();

  let y = 28;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...primary);
  doc.text("RUNNING ACCOUNT BILL", pageW / 2, y, { align: "center" });

  y += 8;

  doc.setDrawColor(...primary);
  doc.setLineWidth(0.8);
  doc.line(margin, y, pageW - margin, y);

  y += 8;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 2,
      lineWidth: 0.2,
    },
    headStyles: {
      fillColor: dark,
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    body: [
      ["RA Bill No", raNo, "RA Bill Date", raDate],
      ["Project Name", title, "PACS / Society", pacsName],
      ["Contractor Name", contractor, "Tender ID", tender?.id || bill.tenderId || ""],
      [
        "Tender Amount",
        `Rs. ${Number(tender?.amount || tender?.baseAmount || 0).toLocaleString("en-IN")}`,
        "Awarded / L1 Amount",
        `Rs. ${Number(
          tender?.awardedAmount || tender?.tenderAwardAmount || tender?.awardedValue || 0
        ).toLocaleString("en-IN")}`,
      ],
      ["Village / Location", tender?.village || tender?.city || "", "District", tender?.district || ""],
    ],
  });

  y = doc.lastAutoTable.finalY + 8;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...primary);
  doc.text("Measurement Abstract", margin, y);

  y += 4;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    theme: "grid",
    head: [
      [
        "Sr",
        "Item Description",
        "Unit",
        "BOQ Qty",
        "Prev Qty",
        "This RA Qty",
        "Total Qty",
        "Rate",
        "Amount",
      ],
    ],
    body: (bill.items || []).map((it, idx) => [
      idx + 1,
      it.description || "",
      it.unit || "",
      Number(it.boqQty || 0).toFixed(3),
      Number(it.executedPrev || 0).toFixed(3),
      Number(it.qtyThisRa || 0).toFixed(3),
      Number(it.executedTotal || Number(it.executedPrev || 0) + Number(it.qtyThisRa || 0)).toFixed(3),
      Number(it.rate || 0).toFixed(2),
      Number(it.amount || 0).toFixed(2),
    ]),
    styles: {
      fontSize: 7,
      cellPadding: 1.6,
      lineWidth: 0.2,
      valign: "top",
    },
    headStyles: {
      fillColor: primary,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
    },
    columnStyles: {
      0: { halign: "center", cellWidth: 8 },
      1: { cellWidth: 62 },
      2: { halign: "center", cellWidth: 13 },
      3: { halign: "right" },
      4: { halign: "right" },
      5: { halign: "right" },
      6: { halign: "right" },
      7: { halign: "right" },
      8: { halign: "right" },
    },
  });

  y = doc.lastAutoTable.finalY + 8;

  autoTable(doc, {
    startY: y,
    margin: { left: pageW - 92, right: margin },
    theme: "grid",
    styles: {
      fontSize: 9,
      cellPadding: 2,
      lineWidth: 0.25,
    },
    body: [
      ["Basic RA Amount", `Rs. ${basicAmount.toLocaleString("en-IN")}`],
      ["GST @ 18%", `Rs. ${gstAmount.toLocaleString("en-IN")}`],
      ["Gross Invoice Value", `Rs. ${grossAmount.toLocaleString("en-IN")}`],
    ],
    columnStyles: {
      0: { fontStyle: "bold" },
      1: { halign: "right", fontStyle: "bold" },
    },
    didParseCell: (data) => {
      if (data.row.index === 2) {
        data.cell.styles.fillColor = light;
        data.cell.styles.fontStyle = "bold";
      }
    },
  });

  y = doc.lastAutoTable.finalY + 12;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...primary);
  doc.text("Certification & Verification", margin, y);

  y += 5;

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    theme: "grid",
    styles: {
      fontSize: 8,
      cellPadding: 3,
      minCellHeight: 18,
    },
    head: [["Prepared By", "Checked By", "Verified By", "Approved By"]],
    body: [
      [
        "Contractor\n\nSignature",
        "PMC Engineer\n\nSignature",
        "Accounts Department\n\nSignature",
        "Chief Executive Officer\nShine Agri Allied & Marketing MSCS Ltd.\n\nSignature & Stamp",
      ],
    ],
    headStyles: {
      fillColor: dark,
      textColor: [255, 255, 255],
      halign: "center",
    },
    bodyStyles: {
      valign: "top",
    },
  });

  y = doc.lastAutoTable.finalY + 8;

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(margin, y, pageW - margin * 2, 20, 2, 2, "F");

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  doc.text(
    "Declaration: This Running Account Bill is generated through Shine Agri PMC Automation System based on contractor submitted measurements and project records. Final payment shall be subject to verification, statutory deductions, GST invoice submission, and CEO approval.",
    margin + 3,
    y + 6,
    { maxWidth: pageW - margin * 2 - 6 }
  );

  addFooter();

  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    addFooter();
  }

  return doc.output("blob");
}